import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { OrgAgentStats } from "../../../api/client.ts";
import SegmentedBar from "../../charts/SegmentedBar.tsx";
import { formatNumber, utcDayLabel } from "../../../utils/format.ts";
import { hitKindLabel } from "../../prospects/hitKinds.tsx";
import { HIT_KIND_COLORS, type HitTimelinePoint } from "../../prospects/hitTimeline.ts";
import Panel from "../../Panel.tsx";
import { agentTypeColor, agentTypeLabel } from "./agentActivity.tsx";

interface AgentOutputPanelProps {
  points: HitTimelinePoint[];
  bucket: "day" | "week";
  agents: OrgAgentStats[];
}

/** Companies found over time by what found them, and how the runs split across agents. */
export default function AgentOutputPanel({ points, bucket, agents }: AgentOutputPanelProps) {
  const hasHits = points.some((p) => p.signal + p.agent + p.manual > 0);
  return (
    <Panel
      fill
      title="Finds over time"
      subtitle={`Companies found per ${bucket}, by what found them; a company found twice counts twice`}
    >
      {!hasHits ? (
        <Typography variant="body2" sx={{ color: "text.secondary", py: 6, textAlign: "center" }}>
          Nothing found in this period.
        </Typography>
      ) : (
        <Box sx={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="var(--mui-palette-divider)" />
              <XAxis dataKey="t" tickFormatter={utcDayLabel} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip
                cursor={{ fill: "var(--mui-palette-action-hover)" }}
                labelFormatter={(t) => utcDayLabel(Number(t))}
                formatter={(value, name) => [formatNumber(Number(value)), hitKindLabel(name as "signal" | "agent" | "manual")]}
                contentStyle={{
                  background: "var(--mui-palette-background-paper)",
                  border: "none",
                  boxShadow: "0 4px 16px rgba(var(--mui-palette-text-primaryChannel) / 0.12)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Bar dataKey="signal" stackId="hits" fill={HIT_KIND_COLORS.signal} isAnimationActive={false} />
              <Bar dataKey="agent" stackId="hits" fill={HIT_KIND_COLORS.agent} isAnimationActive={false} />
              <Bar dataKey="manual" stackId="hits" fill={HIT_KIND_COLORS.manual} isAnimationActive={false} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Typography variant="subtitle2" sx={{ color: "text.secondary", mt: 2.5, mb: 1.25 }}>
        Agent runs
      </Typography>
      <SegmentedBar
        emptyLabel="No agent ran in this period"
        segments={agents.map((a) => ({
          key: a.agent_type,
          label: `${agentTypeLabel(a.agent_type)}${a.companies_found ? ` · ${formatNumber(a.companies_found)} ${a.companies_found === 1 ? "company" : "companies"}` : ""}`,
          value: a.runs,
          color: agentTypeColor(a.agent_type),
        }))}
      />
    </Panel>
  );
}
